import { useAppSelector } from "../../../store/hooks";
import ganttChartStyles from "../../../styles/ganttChart/ganttChart.module.css";
import { Employee, WorkTask } from "../../../types/ganttChart";
import taskStyles from "../../../styles/ganttChart/tasks.module.css";
import DependencyChip from "../../UI/chips/DependencyChip";
import ViolationChip from "../../UI/chips/ViolationChip";
import { Fragment, useEffect, useState } from "react";
import { ReactComponent as WarningIcon } from "../../../assets/icons/warning_yellow.svg";
import DraggableGanttChartBlock from "../ganttChartBlocks/DraggableGanttChartBlock";

const VerticalWorkTask = ({
  id,
  x,
  y,
  width,
  defaultWidth,
  task,
  height,
  employee,
  hideViolations,
  useColorGroupingsAsTeamSplitter,
}: {
  id?: number;
  x: number;
  y: number;
  width: number;
  defaultWidth?: number;
  task: WorkTask;
  height: number;
  employee?: Employee;
  hideViolations?: boolean;
  useColorGroupingsAsTeamSplitter?: boolean;
}) => {
  const [crossTeamAssignment, setCrossTeamAssignment] = useState<
    string | undefined
  >();
  const { verticalColumnWidth, teamGroupColorMaps } = useAppSelector(
    (state) => state.ganttChartSlice
  );

  const { taskBeingDragged } = useAppSelector(
    (state) => state.dragAndDropSlice
  );

  const allowedNumberOfLinesOfText = Math.floor((height! - 5) / 15);

  /**
   *  Check if this task was assigned to a different team in Nexus. This is the case if the task's color code is different
   *  from its employee color group; in this case it was assigned to some team in Nexus, but moved to a different team by the optimization.
   *  This must be indicated visually
   */
  const checkCrossTeamAssignment = (): string | undefined => {
    if (!employee || !teamGroupColorMaps || !task.colorGroup) return;

    if (task.colorGroup !== employee.colorGroup) {
      const taskPreviouslyAssignedToTeam =
        teamGroupColorMaps.colorGroupToTeamNumberMap.get(task.colorGroup);
      if (!taskPreviouslyAssignedToTeam) return;

      return taskPreviouslyAssignedToTeam.split(" ")[1];
    }
  };

  /**
   * Make an initial check if this task is placed in a shift with a different colorGroup than it was originally designated.
   * If this is the case, it has been moved by the optimization, and we must show which team it originally came from.
   * Team, in this case, is the team (color group) to which the shift belongs.
   */
  const showCrossTeamBadge = task.colorGroup !== employee?.colorGroup;
  useEffect(() => {
    setCrossTeamAssignment(checkCrossTeamAssignment());
  }, []);

  const isNotScheduled = task.taskStatusCode === "NOT_SCHEDULED";
  const [isDraggingMatch, setIsDraggingMatch] = useState<boolean>(false);

  useEffect(() => {
    if (taskBeingDragged) {
      setIsDraggingMatch(task.idTaskPlan === taskBeingDragged.task.idTaskPlan);
    } else {
      setIsDraggingMatch(false);
    }
  }, [taskBeingDragged]);

  return (
    <>
      {verticalColumnWidth && height && (
        <>
          {!isDraggingMatch && (
            <DraggableGanttChartBlock
              dragable
              id={id}
              xPosition={x}
              yPosition={y}
              width={width}
              defaultWidth={defaultWidth} //necessary for unplannedTasks, to make task as wide as planned tasks, when dragged.
              height={height}
              taskType="WORKTASK"
              task={task}
              employee={employee}
              isDraggingMatch={isDraggingMatch}
              backgroundColor={
                isNotScheduled
                  ? "var(--col-error-primary)"
                  : "var(--col-card-green)"
              }
              border={
                isNotScheduled
                  ? "4px solid var(--col-error-3)"
                  : "4px solid var(--col-success-primary)"
              }
              allowedNumberOfLinesOfText={allowedNumberOfLinesOfText}
            >
              {showCrossTeamBadge && useColorGroupingsAsTeamSplitter && (
                <div className={taskStyles.crossTeamBadgeContainer}>
                  <p>{crossTeamAssignment}</p>
                </div>
              )}
              <p className={taskStyles.verticalTaskText}>
                <b>{task.client.description}</b>
              </p>
              {allowedNumberOfLinesOfText > 1 && task.subTasks && (
                <ul className={taskStyles.taskList}>
                  {task.subTasks.map((task, index) => (
                    <Fragment key={index}>
                      {allowedNumberOfLinesOfText > index + 1 && (
                        <li className={taskStyles.verticalTaskText}>
                          <div className={ganttChartStyles.bulletPoint}></div>
                          {task.description}
                        </li>
                      )}
                    </Fragment>
                  ))}
                </ul>
              )}
              {/* Todo: come back to, when design has made prioritization rules. */}
              <div className="flex  absolute bottom-[var(--spacing-sm)] right-[var(--spacing-xs)] justify-end">
                {((!task.endLocation.latitude && !task.endLocation.longitude) ||
                  (!task.startLocation.latitude &&
                    !task.startLocation.longitude)) && (
                  <ChipWrapper>
                    <WarningIcon height={14} width={14} />
                  </ChipWrapper>
                )}
                {!hideViolations &&
                  task.violations &&
                  task.violations.length > 0 && (
                    <ChipWrapper>
                      <ViolationChip />
                    </ChipWrapper>
                  )}
                {task.taskDependencies && task.taskDependencies.length > 0 && (
                  <ChipWrapper>
                    <DependencyChip
                      dependencyArray={task.taskDependencies}
                      key={task.idTask}
                    />
                  </ChipWrapper>
                )}
              </div>
            </DraggableGanttChartBlock>
          )}
        </>
      )}
    </>
  );
};

export default VerticalWorkTask;

const ChipWrapper = ({ children }: { children: React.ReactElement }) => {
  return (
    <div
      style={{
        marginRight: "-3px",
        border: "1px solid white",
        borderRadius: "50%",
      }}
    >
      {children}
    </div>
  );
};
